import {
  Alert,
  Body,
  Bold,
  H2,
  H3,
  Lead,
  Meta,
  PrimaryButton,
  SecondaryArrowButton,
  SecondaryButton,
  SubBody,
  SubTitle2,
  Tag,
  TertiaryButton,
  TextField,
} from '@rikstv/shared-components';
import { FC, ReactNode, use, useEffect, useMemo, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Icon, Layout, Loader } from '~/components/shared';
import { FlexBox } from '~/components/shared/layout/FlexBox';
import { SupportFooter } from '~/components/shared/support-footer/SupportFooter';
import { useComparableSubscriptions } from '../choose-subscription/useComparableSubscriptions.ts';
import { displayDataPromise, ispServiceAvailabilityCheck } from './isp.api';
import { InternetCustomerAddress, useInternetFlowStore } from './useInternetStore';

import { DialogCloseButton } from '~/components/shared/dialog/dialog-close-button.tsx';
import { useDialog } from '~/components/shared/dialog/useDialog.ts';
import { ReactComponent as ChannelsSvg } from '~/components/shared/subscription-comparison/images/feature-icons/channels.svg';
import { ReactComponent as InternetSvg } from '~/components/shared/subscription-comparison/images/feature-icons/internet.svg';
import { ReactComponent as StreamingServiceSvg } from '~/components/shared/subscription-comparison/images/feature-icons/tv.svg';
import { useIsMobileView } from '~/hooks/useMediaQuery.ts';
import { v2Routes } from '../v2/v2routes.tsx';
import { getSavingsPercentage, type MappedSubscription, mapSubsriptions, toProductBundle } from './internet.utils.ts';
import { Route_InternetLandingPage } from './InternetLandingPage.tsx';
import classes from './InternetOfferSubPage.module.css';
import { trackClickCheckOtherAddress, trackClickInternetCTA, trackSelectInternetBundle } from '~/analytics/eventTracker.ts';

export const Route_InternetOfferSubPage = 'tilbud';

export const InternetOfferSubPage: FC = () => {
  const navigate = useNavigate();
  const selectedAddress = useInternetFlowStore((s) => s.selectedAddress);
  const availableProducts = useInternetFlowStore((s) => s.availableProducts);
  const setAvailableProducts = useInternetFlowStore((s) => s.setAvailableProducts);
  const [isPending, startTransition] = useTransition();
  const comparableSubscriptions = useComparableSubscriptions('kampanje', 'bolig');
  // sort subscriptions by price and filter out the ones we wont need
  const packages = useMemo(() => {
    if (comparableSubscriptions.data) {
      return mapSubsriptions(comparableSubscriptions.data);
    }
    return [];
  }, [comparableSubscriptions.data]);

  const { legalTerms } = use(displayDataPromise);

  // Fetch data
  useEffect(() => {
    if (selectedAddress?.addrType === 'single') {
      startTransition(async () => {
        // TODO: error handling if data is null, also try-catch request itself
        const ngtProducts = await ispServiceAvailabilityCheck(selectedAddress);
        setAvailableProducts(ngtProducts);
      });
    } else {
      // Redirect if address is not of correct type or null
      // Also when address is reset via button
      navigate(`/${Route_InternetLandingPage}`);
    }
  }, [selectedAddress, setAvailableProducts, navigate]);

  // return null while waiting for redirect
  if (selectedAddress?.addrType !== 'single') {
    return null;
  }
  // Display loading state
  if (isPending) {
    return (
      <Layout>
        <Card>
          <Loader text="" />
          <H2 className={classes.centerText}>Nå sjekker vi hva du kan få...</H2>
          <p className={classes.centerText}>Adresse: {selectedAddress.description}</p>
          <br />
        </Card>
      </Layout>
    );
  }
  if (!availableProducts || availableProducts.length === 0) {
    return (
      <Layout>
        <Card>
          <div>
            <Alert intent="info" variant="inline">
              Beklager! NextGenTel tilbyr ikke internett på denne adressen enda. Du kan fremdeles bestille TV-pakke.
            </Alert>
          </div>
          <br />
          <DisabledsInputField selectedAddress={selectedAddress} />
        </Card>
        <TvPackageOnlyCard />
      </Layout>
    );
  }

  // Display TV+Internet packages
  return (
    <Layout wide>
      <Card>
        <div>
          <Alert intent="success" variant="inline">
            NextGenTel leverer internett på denne adressen. Velg pakken som passer deg nedenfor.
          </Alert>
        </div>
        <br />
        <DisabledsInputField selectedAddress={selectedAddress} />
      </Card>
      <H2>Velg TV- og internettpakke</H2>
      <br />
      <section>
        <FlexBox justifyContent="center">
          {packages.map((pkg) => (
            <PackageCard key={pkg.campaignUrl} package={pkg} />
          ))}
        </FlexBox>
        <br />
        <SubBody style={{ color: 'var(--disabled-text)' }}>{legalTerms}</SubBody>
        <br />
        <TvPackageOnlyCard />
      </section>
      <SupportFooter />
    </Layout>
  );
};

const PackageCard: FC<{ package: MappedSubscription }> = ({ package: pkg }) => {
  const dialog = useDialog();
  const packageName = `${pkg.name} + ${pkg.internet.name}`;
  const isMobile = useIsMobileView();
  const selectedAddress = useInternetFlowStore((s) => s.selectedAddress) as InternetCustomerAddress;
  const setSelectedInternetProduct = useInternetFlowStore((s) => s.setSelectedInternetProduct);
  const navigate = useNavigate();
  const onSelectPackage = () => {
    trackSelectInternetBundle({
      bundle: { name: packageName, price: pkg.currentPrice + pkg.internet.price },
      city: selectedAddress.city,
      postal_code: selectedAddress.postalCode,
    });
    setSelectedInternetProduct(toProductBundle(pkg.internet));
    navigate(pkg.campaignUrl);
  };

  return (
    <div className={classes.packageCard}>
      <Tag className={classes.purpleHat}>Spar {getSavingsPercentage(pkg)}%</Tag>
      <FlexBox direction="column" alignItems={'center'} gap={'24px'}>
        {/* title */}
        <H2 lookLike="title-3">{packageName}</H2>
        {/* price */}
        <div>
          <FlexBox alignItems={'end'}>
            <H3 lookLike="title-2" title={`${pkg.currentPrice},- + ${pkg.internet.price},-`}>
              {pkg.currentPrice + pkg.internet.price},-
            </H3>
            <SubBody>/mnd. i 12 mnd*</SubBody>
          </FlexBox>
          <Meta as="div" className={classes.centerText} title={`${pkg.originalPrice},- + ${pkg.internet.originalPrice},-`}>
            Originalpris {pkg.originalPrice + pkg.internet.originalPrice},- /mnd
          </Meta>
        </div>
        {/* features */}
        <Feature icon={<InternetSvg />} label="Internett" feature={pkg.internet.name} />
        <Feature icon={<ChannelsSvg width="28px" />} label="Antall kanaler" feature={pkg.features.channels} />
        <Feature icon={<StreamingServiceSvg />} label="Antall strømmetjenester" feature={pkg.features.streamingServices.length} />
        {/* actions */}
        <div>
          <FlexBox>
            <TertiaryButton
              onClick={() => {
                trackClickInternetCTA({ link_text: `Les mer om ${packageName}`, ngt_available: true });
                dialog.openModalDialog();
              }}
            >
              Les mer
            </TertiaryButton>
            <dialog ref={dialog.ref}>
              <FlexBox direction="column" gap="1.5rem">
                <FlexBox direction="column">
                  <H3>{packageName}</H3>
                  <Bold>{pkg.name} inkludert</Bold>
                  <ul style={{ paddingLeft: '1.5rem' }}>
                    <li>{pkg.features.channels} TV-kanaler</li>
                    <li>Strømmetjenestene {pkg.features.streamingServices.join(', ')}</li>
                  </ul>
                  <Bold>{pkg.internet.name}</Bold>
                  <Body>
                    Perfekt for familier med 2-5 personer med et normalt hastighetsbehov. Den gir deg en hastighet på 100 Mb/s inn til
                    boligen.
                  </Body>
                  <Bold>Fakturering</Bold>
                  <Body>TV-pakken leveres og faktureres av RiksTV. Internettet leveres og faktureres av NextGenTel.</Body>
                </FlexBox>
                <FlexBox
                  direction={isMobile ? 'column' : undefined}
                  alignItems="center"
                  justifyContent={isMobile ? undefined : 'space-between'}
                >
                  <div>
                    <Lead style={{ '--rds-background-primary': '#00816D' } as any}>
                      {pkg.currentPrice + pkg.internet.price},- /mnd i 12 mnd
                    </Lead>
                    <Meta as="div" className={isMobile ? classes.centerText : undefined}>
                      Originalpris {pkg.originalPrice + pkg.internet.originalPrice},- /mnd
                    </Meta>
                  </div>
                  <PrimaryButton data-testid={`modal-select-${packageName}`} onClick={onSelectPackage}>
                    Bestill nå
                  </PrimaryButton>
                </FlexBox>
              </FlexBox>
              <DialogCloseButton onClick={() => dialog.closeDialog()} />
            </dialog>
            <PrimaryButton data-testid={`select-${packageName}`} onClick={onSelectPackage}>
              Bestill nå
            </PrimaryButton>
          </FlexBox>
        </div>
      </FlexBox>
    </div>
  );
};

const DisabledsInputField: FC<{ selectedAddress: InternetCustomerAddress }> = ({ selectedAddress }) => {
  const setAvailableProducts = useInternetFlowStore((s) => s.setAvailableProducts);
  const setSelectedAddress = useInternetFlowStore((s) => s.setSelectedAddress);
  return (
    <FlexBox className={classes.addressContainer}>
      <TextField disabled value={selectedAddress.description} name="address" label="" style={{ width: '35ch' }} />
      <SecondaryButton
        className={classes.whiteSecondaryBtn}
        onClick={() => {
          trackClickCheckOtherAddress();
          setAvailableProducts(null);
          setSelectedAddress(null);
        }}
      >
        Søk på en annen adresse&nbsp;{' '}
        <Icon type="search" color="white" size={1.5} style={{ fill: selectedAddress ? 'black' : 'var(--disabled-text)' }} />{' '}
      </SecondaryButton>
    </FlexBox>
  );
};

const TvPackageOnlyCard = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobileView();
  const { tvOnlyTitle, tvOnlyDescription } = use(displayDataPromise);
  const btnLabel = 'Se våre TV-pakker';
  return (
    <Card>
      <FlexBox wrap={false} alignItems="center" direction={isMobile ? 'column' : undefined}>
        <FlexBox direction="column" style={{ flex: 1 }} alignItems={isMobile ? 'center' : undefined}>
          <SubTitle2>{tvOnlyTitle}</SubTitle2>
          <span>{tvOnlyDescription}</span>
        </FlexBox>
        <SecondaryArrowButton
          className={classes.whiteSecondaryBtn}
          style={{ minWidth: 'fit-content', height: 'fit-content' }}
          onClick={() => {
            trackClickInternetCTA({ link_text: btnLabel, ngt_available: true, link_url: location.origin + v2Routes.index });
            navigate(v2Routes.index);
          }}
        >
          {btnLabel}
        </SecondaryArrowButton>
      </FlexBox>
    </Card>
  );
};

const Feature: FC<{ icon: ReactNode; label: string; feature: string | number }> = ({ icon, label, feature }) => (
  <div style={{ width: '100%' }}>
    <FlexBox justifyContent="space-between" style={{ borderBottom: '1px solid #DDD', paddingBlock: '8px' }}>
      <FlexBox>
        {icon}
        <span>{label}</span>
      </FlexBox>
      <span>{feature}</span>
    </FlexBox>
  </div>
);
